/**
 * Advanced caching service for API responses
 * Implements multiple caching strategies: memory, localStorage, and sessionStorage
 */

class CacheService {
    constructor() {
        this.memoryCache = new Map();
        this.cacheConfig = {
            // Default cache durations in milliseconds
            short: 5 * 60 * 1000,      // 5 minutes
            medium: 15 * 60 * 1000,    // 15 minutes
            long: 60 * 60 * 1000,      // 1 hour
            permanent: 24 * 60 * 60 * 1000  // 24 hours
        };
    }

    /**
     * Generate a cache key from request config
     */
    generateKey(config) {
        const { method = 'GET', url, params, data } = config;
        let key = `${method}:${url}`;

        // Include query parameters in key
        if (params && Object.keys(params).length > 0) {
            const sortedParams = Object.keys(params)
                .sort()
                .map(k => `${k}=${params[k]}`)
                .join('&');
            key += `?${sortedParams}`;
        }

        // For POST/PUT/PATCH, include a hash of the data
        if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
            const dataHash = this.hashData(data);
            key += `:${dataHash}`;
        }

        return key;
    }

    /**
     * Simple hash function for request data
     */
    hashData(data) {
        const str = JSON.stringify(data);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    }

    /**
     * Set cache entry
     */
    set(key, data, duration = 'medium', storage = 'memory') {
        const expiresAt = Date.now() + this.cacheConfig[duration];
        const cacheEntry = {
            data,
            expiresAt,
            timestamp: Date.now()
        };

        switch (storage) {
            case 'memory':
                this.memoryCache.set(key, cacheEntry);
                break;
            case 'localStorage':
                try {
                    localStorage.setItem(`api_cache_${key}`, JSON.stringify(cacheEntry));
                } catch (e) {
                    console.warn('localStorage cache failed:', e);
                }
                break;
            case 'sessionStorage':
                try {
                    sessionStorage.setItem(`api_cache_${key}`, JSON.stringify(cacheEntry));
                } catch (e) {
                    console.warn('sessionStorage cache failed:', e);
                }
                break;
            default:
                // No-op for unknown storage
                break;
        }
    }

    /**
     * Get cache entry
     */
    get(key, storage = 'memory') {
        let cacheEntry = null;

        switch (storage) {
            case 'memory':
                cacheEntry = this.memoryCache.get(key);
                break;
            case 'localStorage':
                try {
                    const stored = localStorage.getItem(`api_cache_${key}`);
                    cacheEntry = stored ? JSON.parse(stored) : null;
                } catch (e) {
                    console.warn('localStorage cache read failed:', e);
                }
                break;
            case 'sessionStorage':
                try {
                    const stored = sessionStorage.getItem(`api_cache_${key}`);
                    cacheEntry = stored ? JSON.parse(stored) : null;
                } catch (e) {
                    console.warn('sessionStorage cache read failed:', e);
                }
                break;
            default:
                // No-op for unknown storage
                break;
        }

        if (!cacheEntry) return null;

        // Check if expired
        if (Date.now() > cacheEntry.expiresAt) {
            this.delete(key, storage);
            return null;
        }

        return cacheEntry.data;
    }

    /**
     * Delete cache entry
     */
    delete(key, storage = 'memory') {
        switch (storage) {
            case 'memory':
                this.memoryCache.delete(key);
                break;
            case 'localStorage':
                try {
                    localStorage.removeItem(`api_cache_${key}`);
                } catch (e) {
                    console.warn('localStorage cache delete failed:', e);
                }
                break;
            case 'sessionStorage':
                try {
                    sessionStorage.removeItem(`api_cache_${key}`);
                } catch (e) {
                    console.warn('sessionStorage cache delete failed:', e);
                }
                break;
            default:
                // No-op for unknown storage
                break;
        }
    }

    /**
     * Clear all cache entries
     */
    clear(pattern = null, storage = null) {
        const storages = storage ? [storage] : ['memory', 'localStorage', 'sessionStorage'];

        storages.forEach(store => {
            if (store === 'memory') {
                if (pattern) {
                    for (const key of this.memoryCache.keys()) {
                        if (key.includes(pattern)) {
                            this.memoryCache.delete(key);
                        }
                    }
                } else {
                    this.memoryCache.clear();
                }
            } else {
                const storageObj = store === 'localStorage' ? localStorage : sessionStorage;
                const keysToDelete = [];

                try {
                    for (let i = 0; i < storageObj.length; i++) {
                        const key = storageObj.key(i);
                        if (key && key.startsWith('api_cache_')) {
                            const cacheKey = key.replace('api_cache_', '');
                            if (!pattern || cacheKey.includes(pattern)) {
                                keysToDelete.push(key);
                            }
                        }
                    }

                    keysToDelete.forEach(key => storageObj.removeItem(key));
                } catch (e) {
                    console.warn(`${store} cache clear failed:`, e);
                }
            }
        });
    }

    /**
     * Get cache statistics
     */
    getStats() {
        const stats = {
            memory: this.memoryCache.size,
            localStorage: 0,
            sessionStorage: 0
        };

        try {
            for (let i = 0; i < localStorage.length; i++) {
                if (localStorage.key(i)?.startsWith('api_cache_')) {
                    stats.localStorage++;
                }
            }
        } catch (e) { }

        try {
            for (let i = 0; i < sessionStorage.length; i++) {
                if (sessionStorage.key(i)?.startsWith('api_cache_')) {
                    stats.sessionStorage++;
                }
            }
        } catch (e) { }

        return stats;
    }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;

// Export convenience functions
export const clearApiCache = (pattern) => {
    if (!pattern) {
        cacheService.clear(null);
        return;
    }
    const normalized = pattern.replace(/^\/+|\/+$/g, '');
    cacheService.clear(normalized || null);
};
export const getCacheStats = () => cacheService.getStats();
